// Extracted from shared/contracts.ts; behavior and declaration bodies preserved.
import type { Notation, ThemePreset, PaletteId, EntityDetailLevel, ThemeTokens, RelationshipStyle, EntityDisplayOptions } from "../model/contracts.js";

export type ToolbarId = "commands" | "models" | "diagram" | "navigation" | "layout" | "export";
export type DockZone = "home" | "header" | "header-secondary" | "commands" | "tabs" | "diagram" | "top" | "bottom" | "left" | "right" | "floating";
export interface ToolbarPlacement { visible: boolean; zone: DockZone; x?: number; y?: number; order?: number }
export interface PaletteCommandPlacement { palette: Exclude<ToolbarId, "models">; order: number; alwaysShow: boolean }
export interface WorkspaceLayout { commands?: Record<string, PaletteCommandPlacement>; version?: 1; locked: boolean; toolbars: Partial<Record<ToolbarId, ToolbarPlacement>>; explorerWidth?: number; propertiesWidth?: number }
export interface ExportBackground { mode: "transparent" | "solid"; color: string; provenance?: "default" | "explicit" }
export type ModelTypePresentation = Partial<Record<"conceptual" | "logical" | "physical", Partial<Pick<Preferences, "fontSize" | "titleSize" | "detailLevel" | "entityDisplay" | "autoSizeEntities" | "resizeOnDetailChange" | "centerEntityNames" | "showEntityDescription">>>>;
export interface Preferences {
  theme: ThemePreset;
  accent: string;
  fontFamily: string;
  fontSize: number;
  titleSize: number;
  density: "comfortable" | "compact";
  settingsView: "icons" | "list";
  workspace: "balanced" | "focus" | "inspector-left";
  saveMode: "automatic" | "manual";
  /** 0 coalesces completed changes; positive values save on a fixed cadence. */
  autosaveIntervalSeconds: number;
  showAutosaveSuccess: boolean;
  receiveMode: "automatic" | "notify";
  autoAddId: boolean;
  autoAddDescription: boolean;
  nullabilityWording: "null" | "mandatory";
  logicalPrimaryKeyPolicy: "business-first" | "retain-source";
  removeSurrogatesOnDerive: boolean;
  railLabels: boolean;
  grid: boolean;
  snap: boolean;
  notation: Notation;
  experience: "guided" | "standard" | "expert";
  customTheme: ThemeTokens;
  paletteId: PaletteId;
  detailLevel: EntityDetailLevel;
  diagramDetailLevels: Record<string, EntityDetailLevel>;
  entityDisplay?: EntityDisplayOptions;
  diagramEntityDisplay?: Record<string, EntityDisplayOptions>;
  showAttributePropertiesOnSelection?: boolean;
  autoAdjustNearbyEntities?: boolean;
  autoAdjustRelationships?: boolean;
  layoutControls: "icons" | "compact";
  propertyPresentation: "popup" | "inspector";
  relationshipStyle: RelationshipStyle;
  workspaceLayout: WorkspaceLayout;
  personalWorkspaceLayout?: WorkspaceLayout | null;
  interfaceScale?: number;
  exportBackgrounds: { png: ExportBackground; svg: ExportBackground };
  modelTypePresentation: ModelTypePresentation;
  startupInterfaceSize: "small" | "medium" | "large";
  relationshipCreationMode: "endpoints" | "dialog";
  layoutPreviewMode: "single" | "all";
  showRelationshipNames: boolean;
  showMinimap?: boolean;
  preserveManualRoutes: boolean;
  /** True means the Explorer section is expanded. */
  explorerSections: { models: boolean; diagrams: boolean; objects: boolean; relationships?: boolean; relationshipsChoice?: "expanded" | "collapsed" };
  attributeDisplayLimit: number;
  showAllAttributes: boolean;
  allowUnlimitedCanvasExpansion: boolean;
  centerRelationshipEndpoints: boolean;
  avoidParentTopAttachment: boolean;
  relationshipLaneGap: number;
  autoSizeEntities: boolean;
  resizeOnDetailChange: boolean;
  centerEntityNames: boolean;
  showEntityDescription: boolean;
  entityPreviewEnabled: boolean;
  entityPreviewDelay: number;
}
export const defaultPreferences: Preferences = {
  theme: "light",
  accent: "#256d62",
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: 13,
  titleSize: 14,
  density: "comfortable",
  settingsView: "icons",
  workspace: "balanced",
  saveMode: "manual",
  autosaveIntervalSeconds: 0,
  showAutosaveSuccess: false,
  receiveMode: "notify",
  autoAddId: true,
  autoAddDescription: true,
  nullabilityWording: "null",
  logicalPrimaryKeyPolicy: "business-first",
  removeSurrogatesOnDerive: false,
  railLabels: true,
  grid: true,
  snap: true,
  notation: "crow-foot",
  experience: "standard",
  customTheme: { canvas: "#f3f6fa", surface: "#ffffff", header: "#e8edf5", text: "#172438", muted: "#5d6d82", border: "#c8d3e2", accent: "#2563eb", relationship: "#60758f" },
  paletteId: "teal",
  detailLevel: "all",
  diagramDetailLevels: {},
  showAttributePropertiesOnSelection: true,
  autoAdjustNearbyEntities: true,
  autoAdjustRelationships: true,
  layoutControls: "icons",
  propertyPresentation: "popup",
  workspaceLayout: { version: 1, locked: true, toolbars: {
    commands: { visible: true, zone: "home" }, models: { visible: true, zone: "home" },
    diagram: { visible: true, zone: "commands", order: 0 }, navigation: { visible: true, zone: "commands", order: 1 },
    layout: { visible: true, zone: "header-secondary", order: 0 }, export: { visible: true, zone: "header-secondary", order: 1 },
  } },
  exportBackgrounds: { png: { mode: "transparent", color: "#ffffff", provenance: "default" }, svg: { mode: "transparent", color: "#ffffff", provenance: "default" } },
  modelTypePresentation: { conceptual: { detailLevel: "name", showEntityDescription: true } },
  startupInterfaceSize: "medium",
  relationshipCreationMode: "endpoints",
  layoutPreviewMode: "single",
  showRelationshipNames: true,
  preserveManualRoutes: true,
  explorerSections: { models: true, diagrams: true, objects: true, relationships: false },
  attributeDisplayLimit: 20,
  showAllAttributes: false,
  allowUnlimitedCanvasExpansion: false,
  centerRelationshipEndpoints: true,
  avoidParentTopAttachment: true,
  relationshipLaneGap: 18,
  autoSizeEntities: true,
  resizeOnDetailChange: false,
  centerEntityNames: true,
  showEntityDescription: false,
  entityPreviewEnabled: true,
  entityPreviewDelay: 3000,
  relationshipStyle: { width: 1.7, label: { orientation: "aligned", placement: "above", offset: 12, position: 0.5, visible: true } },
};
